/**
 * Per-entity payroll operational config (#22729 backend, #22730 this pass) —
 * self-fetching hooks, NOT a `dev_*.ts`/`useDevBackendData` boot-time seed.
 * Mirrors `use_data_centre.ts`/`use_employees.ts`: the real bearer client
 * (`getTrpcClient()`, `lib/trpc.ts`) and the shared `useOAuthEntityContext()`
 * helper. Two independent read models live here, both consumed by the Data
 * Centre's bank/loans tabs (`use_data_centre.ts`) AND the Settings screen's
 * Payroll tab (`use_settings.ts`) — one shared fetch/state-machine per
 * concept rather than each screen re-implementing its own:
 *
 *  - `usePayrollSettings()` — `payroll.getPayrollSettings`/
 *    `updatePayrollSettings`, the 10-field `entity_payroll_settings` row
 *    (disbursement account + file format, salary-advance/staff-loan rate +
 *    installment caps, default pay frequency, rounding rule, proration
 *    method, overtime hours divisor).
 *  - `usePaymentSourceAccounts()` — `payroll.listPaymentSourceAccounts`
 *    (#22324 C5), the narrow bank/cash-asset picker feeding the disbursement
 *    -account field. Deliberately a SEPARATE fetch/hook from
 *    `usePayrollSettings()`: the Settings Payroll tab never needs the
 *    account list at all, so bundling the two into one fetch would cost it
 *    an unnecessary round trip.
 *
 * ── No mock fallback ─────────────────────────────────────────────────────
 *
 * Same stance as every other real, OAuth-backed hook in this app: a
 * `FORBIDDEN` caused specifically by the missing `payroll` add-on
 * entitlement (`isPayrollAddonRequiredError`, `lib/trpc.ts`) resolves an
 * explicit `entitlement_required` state; every OTHER failure resolves the
 * generic `error` state. Neither ever falls back to
 * `MOCK_DISBURSEMENT_ACCOUNT`/`MOCK_LOAN_POLICIES`/`MOCK_PAYROLL_SETTINGS` —
 * all three are deleted from `mock_data.ts` (this module is their
 * production replacement).
 */
import * as React from 'react';
import { getTrpcClient, isPayrollAddonRequiredError } from '../lib/trpc.js';
import { useAuth } from '../lib/auth/auth_context.js';
import {
  useOAuthEntityContext,
  type OAuthEntityContext,
} from '../lib/auth/oauth_entity_context.js';
import { isWriteCapableGrant } from './use_pay_runs.js';
import type {
  PayrollSettingsPatch,
  RealPayrollSettingsRecord,
} from './wagepro_store_payroll_settings.js';
import type { PaymentSourceAccountOption } from './wagepro_store';

/** Input every `payroll.*` call in this module needs to identify the entity. */
export interface PayrollSettingsFetchInput {
  firm_id: string;
  entity_id: string;
}

/** The two ways a real fetch/save in this module can fail, mirroring `DataCentreLoadFailure` (`use_data_centre.ts`). */
export type PayrollSettingsLoadFailure = 'entitlement_required' | 'error';

function classifyFailure(err: unknown): PayrollSettingsLoadFailure {
  return isPayrollAddonRequiredError(err) ? 'entitlement_required' : 'error';
}

// ── payroll.getPayrollSettings / updatePayrollSettings ──────────────────────

export interface PayrollSettingsFetchDeps {
  getPayrollSettings: (
    input: PayrollSettingsFetchInput,
  ) => Promise<{ settings: RealPayrollSettingsRecord }>;
}

function defaultPayrollSettingsFetchDeps(): PayrollSettingsFetchDeps {
  const client = getTrpcClient();
  return {
    getPayrollSettings: (input) =>
      client.payroll.getPayrollSettings.query(input),
  };
}

export type PayrollSettingsFetchResult =
  | { outcome: 'success'; settings: RealPayrollSettingsRecord }
  | { outcome: 'failure'; failure: PayrollSettingsLoadFailure };

/**
 * Resolves the entity's real payroll settings, or classifies why it
 * couldn't. Exported (and DI'd via `deps`) so the classification is
 * unit-testable against a fake client — mirrors `use_data_centre.ts`'s
 * `fetchPayItemRecords`.
 */
export async function fetchPayrollSettingsRecord(
  input: PayrollSettingsFetchInput,
  deps?: PayrollSettingsFetchDeps,
  isCancelled: () => boolean = () => false,
): Promise<PayrollSettingsFetchResult> {
  try {
    const resolvedDeps = deps ?? defaultPayrollSettingsFetchDeps();
    const result = await resolvedDeps.getPayrollSettings(input);
    return { outcome: 'success', settings: result.settings };
  } catch (err) {
    if (!isCancelled()) {
      console.error(
        '[wagepro-web] Failed to resolve real payroll settings:',
        err,
      );
    }
    return { outcome: 'failure', failure: classifyFailure(err) };
  }
}

export type PayrollSettingsState =
  | { status: 'loading' }
  /** The OAuth grant has no bound entity — structurally unreachable in practice (`main.tsx`'s `AuthGate` gates the whole app on this first), handled defensively anyway. */
  | { status: 'no_entity' }
  /** The entity is missing the `payroll` add-on entitlement (#21675). NEVER render `MOCK_PAYROLL_SETTINGS`/`MOCK_LOAN_POLICIES`/`MOCK_DISBURSEMENT_ACCOUNT` here. */
  | { status: 'entitlement_required' }
  | { status: 'error' }
  | { status: 'ready'; settings: RealPayrollSettingsRecord };

/**
 * Pure derivation, exported separately so it is unit-testable without
 * rendering React — mirrors `resolveDataCentreState`'s precedence exactly:
 * `no_entity` always wins, then a resolved read wins over a stale failure,
 * then `entitlement_required`, then `error`, then `loading`.
 */
export function resolvePayrollSettingsState(input: {
  entityContext: OAuthEntityContext;
  settings: RealPayrollSettingsRecord | undefined;
  failure: PayrollSettingsLoadFailure | undefined;
}): PayrollSettingsState {
  if (input.entityContext.status === 'no_entity') {
    return { status: 'no_entity' };
  }
  if (input.settings !== undefined) {
    return { status: 'ready', settings: input.settings };
  }
  if (input.failure === 'entitlement_required') {
    return { status: 'entitlement_required' };
  }
  if (input.failure === 'error') {
    return { status: 'error' };
  }
  return { status: 'loading' };
}

export type SavePayrollSettingsResult =
  | { outcome: 'success'; settings: RealPayrollSettingsRecord }
  | { outcome: 'failure'; failure: PayrollSettingsLoadFailure };

export interface PayrollSettingsMutationDeps {
  updatePayrollSettings: (
    input: PayrollSettingsFetchInput & PayrollSettingsPatch,
  ) => Promise<{ settings: RealPayrollSettingsRecord }>;
}

function defaultPayrollSettingsMutationDeps(): PayrollSettingsMutationDeps {
  const client = getTrpcClient();
  return {
    updatePayrollSettings: (input) =>
      client.payroll.updatePayrollSettings.mutate(input),
  };
}

/**
 * Calls the real `payroll.updatePayrollSettings`. Never throws — every
 * failure mode resolves `{ outcome: 'failure', failure }` with one
 * actionable `console.error`, mirroring `use_data_centre.ts`'s
 * `saveAllowance` never-throw contract, including resolving `deps` INSIDE
 * the try (a synchronous `getTrpcClient()` throw must not escape as an
 * unhandled rejection).
 */
export async function savePayrollSettings(
  patch: PayrollSettingsPatch,
  entityInput: PayrollSettingsFetchInput,
  deps?: PayrollSettingsMutationDeps,
): Promise<SavePayrollSettingsResult> {
  try {
    const resolvedDeps = deps ?? defaultPayrollSettingsMutationDeps();
    const result = await resolvedDeps.updatePayrollSettings({
      ...entityInput,
      ...patch,
    });
    return { outcome: 'success', settings: result.settings };
  } catch (err) {
    console.error('[wagepro-web] payroll settings: save failed:', err);
    if (isPayrollAddonRequiredError(err)) {
      return { outcome: 'failure', failure: 'entitlement_required' };
    }
    return { outcome: 'failure', failure: 'error' };
  }
}

export interface UsePayrollSettingsResult {
  state: PayrollSettingsState;
  /** Convenience projection: `undefined` for every non-`ready` state. */
  settings: RealPayrollSettingsRecord | undefined;
  is_loading: boolean;
  /** Re-runs the fetch from scratch — wired to the `error`/`entitlement_required` states' "try again" action. */
  retry: () => void;
  /**
   * Saves a partial settings patch through the real backend. `undefined`
   * when the write path isn't reachable — either the read hasn't resolved
   * `ready` yet, or the OAuth grant isn't write-capable
   * (`!isWriteCapableGrant(grant)`) — so callers hide the save affordance
   * entirely rather than offering a button that would 403.
   */
  save:
    | ((patch: PayrollSettingsPatch) => Promise<SavePayrollSettingsResult>)
    | undefined;
}

/** Per-entity payroll operational config: disbursement, loan policy, pay-run mechanics. */
export function usePayrollSettings(): UsePayrollSettingsResult {
  const entityContext = useOAuthEntityContext();
  const { grant } = useAuth();
  const [settings, setSettings] = React.useState<
    RealPayrollSettingsRecord | undefined
  >(undefined);
  const [failure, setFailure] = React.useState<
    PayrollSettingsLoadFailure | undefined
  >(undefined);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (entityContext.status !== 'ready') return;
    const firmId: string = entityContext.firm_id;
    const entityId: string = entityContext.entity_id;
    let cancelled = false;
    setSettings(undefined);
    setFailure(undefined);

    async function load(): Promise<void> {
      const result = await fetchPayrollSettingsRecord(
        { firm_id: firmId, entity_id: entityId },
        undefined,
        () => cancelled,
      );
      if (cancelled) return;
      if (result.outcome === 'success') {
        setSettings(result.settings);
      } else {
        setFailure(result.failure);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, reloadToken]);

  const state = React.useMemo(
    () => resolvePayrollSettingsState({ entityContext, settings, failure }),
    [entityContext, settings, failure],
  );

  const readyEntity =
    state.status === 'ready' && entityContext.status === 'ready'
      ? entityContext
      : null;
  const canWrite = readyEntity !== null && isWriteCapableGrant(grant);

  const save = canWrite
    ? async (
        patch: PayrollSettingsPatch,
      ): Promise<SavePayrollSettingsResult> => {
        const result = await savePayrollSettings(patch, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        });
        // Applied directly from the mutation's own response rather than a
        // reload-token re-fetch -- `updatePayrollSettings` already returns
        // the full post-patch settings object, so a second round trip would
        // be redundant.
        if (result.outcome === 'success') {
          setSettings(result.settings);
        }
        return result;
      }
    : undefined;

  return {
    state,
    settings: state.status === 'ready' ? state.settings : undefined,
    is_loading: state.status === 'loading',
    retry: () => {
      setReloadToken((token) => token + 1);
    },
    save,
  };
}

// ── payroll.listPaymentSourceAccounts ────────────────────────────────────────

export interface PaymentSourceAccountsFetchDeps {
  listPaymentSourceAccounts: (
    input: PayrollSettingsFetchInput,
  ) => Promise<{ accounts: PaymentSourceAccountOption[] }>;
}

function defaultPaymentSourceAccountsFetchDeps(): PaymentSourceAccountsFetchDeps {
  const client = getTrpcClient();
  return {
    listPaymentSourceAccounts: (input) =>
      client.payroll.listPaymentSourceAccounts.query(input),
  };
}

export type PaymentSourceAccountsFetchResult =
  | { outcome: 'success'; accounts: PaymentSourceAccountOption[] }
  | { outcome: 'failure'; failure: PayrollSettingsLoadFailure };

export async function fetchPaymentSourceAccounts(
  input: PayrollSettingsFetchInput,
  deps?: PaymentSourceAccountsFetchDeps,
  isCancelled: () => boolean = () => false,
): Promise<PaymentSourceAccountsFetchResult> {
  try {
    const resolvedDeps = deps ?? defaultPaymentSourceAccountsFetchDeps();
    const result = await resolvedDeps.listPaymentSourceAccounts(input);
    return { outcome: 'success', accounts: result.accounts };
  } catch (err) {
    if (!isCancelled()) {
      console.error(
        '[wagepro-web] Failed to resolve the payment-source account picker:',
        err,
      );
    }
    return { outcome: 'failure', failure: classifyFailure(err) };
  }
}

export type PaymentSourceAccountsState =
  | { status: 'loading' }
  | { status: 'no_entity' }
  | { status: 'entitlement_required' }
  | { status: 'error' }
  | { status: 'ready'; accounts: PaymentSourceAccountOption[] };

export function resolvePaymentSourceAccountsState(input: {
  entityContext: OAuthEntityContext;
  accounts: PaymentSourceAccountOption[] | undefined;
  failure: PayrollSettingsLoadFailure | undefined;
}): PaymentSourceAccountsState {
  if (input.entityContext.status === 'no_entity') {
    return { status: 'no_entity' };
  }
  if (input.accounts !== undefined) {
    return { status: 'ready', accounts: input.accounts };
  }
  if (input.failure === 'entitlement_required') {
    return { status: 'entitlement_required' };
  }
  if (input.failure === 'error') {
    return { status: 'error' };
  }
  return { status: 'loading' };
}

export interface UsePaymentSourceAccountsResult {
  state: PaymentSourceAccountsState;
  accounts: PaymentSourceAccountOption[];
  is_loading: boolean;
  retry: () => void;
}

/** The disbursement-account picker's own narrow bank/cash-asset list (#22324 C5's `payroll.listPaymentSourceAccounts`). */
export function usePaymentSourceAccounts(): UsePaymentSourceAccountsResult {
  const entityContext = useOAuthEntityContext();
  const [accounts, setAccounts] = React.useState<
    PaymentSourceAccountOption[] | undefined
  >(undefined);
  const [failure, setFailure] = React.useState<
    PayrollSettingsLoadFailure | undefined
  >(undefined);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (entityContext.status !== 'ready') return;
    const firmId: string = entityContext.firm_id;
    const entityId: string = entityContext.entity_id;
    let cancelled = false;
    setAccounts(undefined);
    setFailure(undefined);

    async function load(): Promise<void> {
      const result = await fetchPaymentSourceAccounts(
        { firm_id: firmId, entity_id: entityId },
        undefined,
        () => cancelled,
      );
      if (cancelled) return;
      if (result.outcome === 'success') {
        setAccounts(result.accounts);
      } else {
        setFailure(result.failure);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, reloadToken]);

  const state = React.useMemo(
    () =>
      resolvePaymentSourceAccountsState({ entityContext, accounts, failure }),
    [entityContext, accounts, failure],
  );

  return {
    state,
    accounts: state.status === 'ready' ? state.accounts : [],
    is_loading: state.status === 'loading',
    retry: () => {
      setReloadToken((token) => token + 1);
    },
  };
}
